import { CalendarRange, ChartNoAxesGantt, FileText, Layers, List, Search, Tag, UserPen, Users, X } from 'lucide-react';
import { type ReactNode, useLayoutEffect, useRef, useState } from 'react';
import { cn } from '@/lib/utils';
import type { RegionalDateLocale } from '@/utils/date-utils';
import {
  DateFilterContent,
  FacetList,
  FilterButton,
  summarizeDateRange,
  summarizeSelection,
  TagTree,
} from './FilterControls';
import {
  TOOLBAR_BUTTON_ACTIVE_CLASSNAME,
  TOOLBAR_BUTTON_CLASSNAME,
  TOOLBAR_LABEL_CLASSNAME,
} from './toolbar-styles';
import type { ChronologyFilters } from './useChronologyFilters';

export type ChronologyViewMode = 'list' | 'timeline';

type FilterKey = 'dates' | 'tags' | 'entities' | 'documents' | 'creators' | 'chronologies';

const VIEW_OPTIONS: { value: ChronologyViewMode; label: string; icon: typeof List }[] = [
  { value: 'list', label: 'List', icon: List },
  { value: 'timeline', label: 'Timeline', icon: ChartNoAxesGantt },
];

export function ViewModeToggle({ value, onChange }: { value: ChronologyViewMode; onChange: (value: ChronologyViewMode) => void }) {
  return (
    <div role="radiogroup" aria-label="View" className="inline-flex h-8 shrink-0 items-center rounded-md bg-gray-100 p-0.5">
      {VIEW_OPTIONS.map(option => {
        const selected = option.value === value;
        const Icon = option.icon;
        return (
          // biome-ignore lint/a11y/useSemanticElements: segmented control styled as buttons.
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={selected}
            aria-label={option.label}
            title={`${option.label} view`}
            onClick={() => onChange(option.value)}
            className={cn(
              'inline-flex h-full items-center gap-1.5 rounded-[5px] px-2.5 text-[13px] transition-colors',
              'focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-gray-300/60',
              selected
                ? 'bg-white font-medium text-gray-900 shadow-xs'
                : 'text-gray-500 hover:text-gray-800',
            )}
          >
            <Icon className={cn('h-3.5 w-3.5 shrink-0', selected ? 'text-gray-700' : 'text-gray-400')} />
            <span className={TOOLBAR_LABEL_CLASSNAME}>{option.label}</span>
          </button>
        );
      })}
    </div>
  );
}

/** Width of the filters and view/export controls on one line, and whether that line still fits beside the search. */
function measureControlsRow(bar: HTMLElement, controls: HTMLElement): { onOwnRow: boolean; contentWidth: number } {
  const search = bar.querySelector<HTMLElement>('[data-chron-search]');
  const controlsGap = Number.parseFloat(getComputedStyle(controls).columnGap) || 0;
  let contentWidth = 0;
  let counted = 0;
  for (const child of Array.from(controls.children) as HTMLElement[]) {
    const width = Math.max(child.offsetWidth, child.scrollWidth);
    if (width === 0) continue;
    contentWidth += width;
    counted += 1;
  }
  if (counted > 1) contentWidth += controlsGap * (counted - 1);
  if (!search) return { onOwnRow: false, contentWidth };

  const style = getComputedStyle(search);
  const basis = Number.parseFloat(style.flexBasis);
  const min = Number.parseFloat(style.minWidth);
  const max = Number.parseFloat(style.maxWidth);
  const searchWidth = Math.min(
    Math.max(Number.isFinite(basis) ? basis : min || 0, min || 0),
    Number.isFinite(max) ? max : Number.POSITIVE_INFINITY,
  );
  const barGap = Number.parseFloat(getComputedStyle(bar).columnGap) || 0;
  return {
    contentWidth,
    onOwnRow: contentWidth + barGap + searchWidth > bar.clientWidth + 1,
  };
}

export interface ChronologyFilterBarProps {
  filters: ChronologyFilters;
  locale: RegionalDateLocale;
  colorByCategory?: ReadonlyMap<string, string | null>;
  showChronologyFilter: boolean;
  /** View and output controls, kept at the right end of the toolbar. */
  trailing?: ReactNode;
}

export function ChronologyFilterBar({
  filters,
  locale,
  colorByCategory,
  showChronologyFilter,
  trailing,
}: ChronologyFilterBarProps) {
  const [openFilter, setOpenFilter] = useState<FilterKey | null>(null);
  const barRef = useRef<HTMLDivElement>(null);
  const controlsRef = useRef<HTMLDivElement>(null);
  const [controlsLayout, setControlsLayout] = useState({ onOwnRow: false, contentWidth: 0 });

  useLayoutEffect(() => {
    const bar = barRef.current;
    const controls = controlsRef.current;
    if (!bar || !controls) return;

    const measure = () => {
      const next = measureControlsRow(bar, controls);
      setControlsLayout(current =>
        current.onOwnRow === next.onOwnRow && current.contentWidth === next.contentWidth ? current : next,
      );
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(bar);
    return () => observer.disconnect();
  });
  const openChange = (key: FilterKey) => (open: boolean) =>
    setOpenFilter(current => (open ? key : current === key ? null : current));

  const tagSummary = (() => {
    const labels: string[] = [];
    for (const category of filters.tagCategoryNames) {
      const state = filters.tagSelectionState(category);
      if (state === 'none') continue;
      const tags = filters.tagsByCategoryName.get(category) ?? [];
      if (state === 'all') labels.push(category);
      else labels.push(...tags.filter(tag => filters.isTagSelected(tag.key, tag.category)).map(tag => tag.label || tag.key));
    }
    return summarizeSelection(labels);
  })();

  const chronologyNames = filters.chronologyOptions
    .filter(option => filters.activeChronologies.has(option.type))
    .map(option => option.name);

  return (
    <div className="w-full">
      <div ref={barRef} className="flex flex-wrap items-center gap-2">
        <div data-chron-search className="relative min-w-[12rem] max-w-[22rem] flex-1 basis-60">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-gray-400" />
          <input
            type="search"
            value={filters.searchQuery}
            onChange={event => filters.setSearchQuery(event.target.value)}
            placeholder="Search events, names or documents"
            aria-label="Search events"
            className={cn(
              'h-8 w-full rounded-md border bg-white pl-8 pr-8 text-[13px] text-gray-800 transition-colors placeholder:text-gray-400',
              'focus:border-gray-400 focus:outline-hidden focus:ring-2 focus:ring-gray-200/70 [&::-webkit-search-cancel-button]:hidden',
              filters.searchQuery ? 'border-gray-300' : 'border-gray-200 hover:border-gray-300',
            )}
          />
          {filters.searchQuery && (
            <button
              type="button"
              onClick={() => filters.setSearchQuery('')}
              className="absolute right-1.5 top-1/2 flex h-5 w-5 -translate-y-1/2 items-center justify-center rounded-sm text-gray-400 hover:bg-gray-100 hover:text-gray-700"
              aria-label="Clear search"
              title="Clear search"
            >
              <X className="h-3 w-3" />
            </button>
          )}
        </div>

        <div
          ref={controlsRef}
          className={cn(
            'flex min-w-0 items-center gap-2 [&>*]:shrink-0',
            controlsLayout.onOwnRow ? 'basis-full flex-wrap' : 'flex-1 flex-nowrap',
          )}
          style={controlsLayout.onOwnRow ? undefined : { minWidth: controlsLayout.contentWidth }}
        >
        <FilterButton
          icon={CalendarRange}
          label="Dates"
          value={summarizeDateRange(filters.dateRange, locale)}
          onClear={() => filters.setDateRange(undefined)}
          open={openFilter === 'dates'}
          onOpenChange={openChange('dates')}
          contentClassName="w-fit max-w-[calc(100vw-2rem)]"
        >
          <DateFilterContent
            dateRange={filters.dateRange}
            eventYears={filters.eventYears}
            onChange={filters.setDateRange}
          />
        </FilterButton>

        {filters.tagCategoryNames.length > 0 && (
          <FilterButton
            icon={Tag}
            label="Tags"
            value={tagSummary}
            onClear={filters.clearTagSelection}
            open={openFilter === 'tags'}
            onOpenChange={openChange('tags')}
            contentClassName="w-[300px]"
          >
            <TagTree
              categories={filters.tagCategoryNames}
              tagsByCategory={filters.tagsByCategoryName}
              categoryCounts={filters.categoryCounts}
              typeCounts={filters.typeCounts}
              colorByCategory={colorByCategory}
              selectedCount={filters.selectedTagCount}
              categoryState={filters.tagSelectionState}
              isTagSelected={filters.isTagSelected}
              onToggleCategory={filters.toggleCategorySelection}
              onToggleTag={filters.toggleTagSelection}
              onOnlyTag={filters.showOnlyType}
              onClear={filters.clearTagSelection}
            />
          </FilterButton>
        )}

        {filters.entityEventCounts.size > 0 && (
          <FilterButton
            icon={Users}
            label="Entities"
            value={summarizeSelection(Array.from(filters.activeEntities))}
            onClear={filters.clearEntities}
            open={openFilter === 'entities'}
            onOpenChange={openChange('entities')}
          >
            <FacetList
              counts={filters.entityEventCounts}
              activeValues={filters.activeEntities}
              searchPlaceholder="Search people and organisations..."
              onToggle={filters.toggleEntity}
              onClear={filters.clearEntities}
            />
          </FilterButton>
        )}

        {filters.sourceEventCounts.size > 0 && (
          <FilterButton
            icon={FileText}
            label="Documents"
            value={summarizeSelection(Array.from(filters.activeSources))}
            onClear={filters.clearSources}
            open={openFilter === 'documents'}
            onOpenChange={openChange('documents')}
            contentClassName="w-[320px]"
          >
            <FacetList
              counts={filters.sourceEventCounts}
              activeValues={filters.activeSources}
              searchPlaceholder="Search source documents..."
              onToggle={filters.toggleSource}
              onClear={filters.clearSources}
            />
          </FilterButton>
        )}

        {showChronologyFilter && (
          <FilterButton
            icon={Layers}
            label="Chronologies"
            value={summarizeSelection(chronologyNames)}
            onClear={filters.clearChronologies}
            open={openFilter === 'chronologies'}
            onOpenChange={openChange('chronologies')}
            contentClassName="w-[260px]"
          >
            <FacetList
              counts={new Map(filters.chronologyOptions.map(option => [option.type, option.count]))}
              labels={new Map(filters.chronologyOptions.map(option => [option.type, option.name]))}
              activeValues={filters.activeChronologies}
              searchPlaceholder="Search chronologies..."
              onToggle={filters.toggleChronology}
              onClear={filters.clearChronologies}
            />
          </FilterButton>
        )}

        {filters.hasMultipleCreators && (
          <FilterButton
            icon={UserPen}
            label="Added by"
            value={summarizeSelection(Array.from(filters.activeCreators))}
            onClear={filters.clearCreators}
            open={openFilter === 'creators'}
            onOpenChange={openChange('creators')}
            contentClassName="w-[240px]"
          >
            <FacetList
              counts={filters.creatorEventCounts}
              activeValues={filters.activeCreators}
              searchPlaceholder="Search..."
              onToggle={filters.toggleCreator}
              onClear={filters.clearCreators}
            />
          </FilterButton>
        )}

        {filters.hasRelevanceScores && (
          <div className={cn(TOOLBAR_BUTTON_CLASSNAME, 'gap-2 hover:border-gray-200 hover:text-gray-600', filters.relevanceThreshold > 0 && TOOLBAR_BUTTON_ACTIVE_CLASSNAME)}>
            <span className={TOOLBAR_LABEL_CLASSNAME}>Relevance</span>
            <input
              type="range"
              min={0}
              max={100}
              step={5}
              value={filters.relevanceThreshold * 100}
              aria-label="Minimum relevance"
              onChange={event => filters.setRelevanceThreshold(Number(event.target.value) / 100)}
              className="h-1.5 w-24 cursor-pointer appearance-none rounded-lg bg-gray-200 accent-gray-700"
            />
            <span className="w-7 text-[11px] tabular-nums text-gray-400">
              {filters.relevanceThreshold > 0 ? `${Math.round(filters.relevanceThreshold * 100)}%` : 'All'}
            </span>
          </div>
        )}

        {trailing && <div className="ml-auto flex shrink-0 items-center gap-2 pl-2">{trailing}</div>}
        </div>
      </div>
    </div>
  );
}
